import { Liquid } from 'liquidjs'
import { useParams } from 'react-router-dom'
import { BulkField } from '../constants'
import { useMedication, usePatient, useProducts } from '../hooks'
import { useAppSelector } from '../store/hooks'
import { AdmissionIncluded } from '../types/admission.type'
import { ProductInventoryResMock } from '../types/inventory.type'
import { NoteIncluded } from '../types/note.type'
import { ApiStrength } from '../types/product.type'
import { showToast } from './toast'

export const createOption = (label: string) => ({
  label,
  value: label
})

export const formatMedicationDisplayName = ({
  drugName,
  fallback = '-',
  form,
  strength
}: {
  drugName?: string | null
  fallback?: string
  form?: string | null
  strength?: string | null
}) => {
  const label = [form, drugName, strength]
    .map(item => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
    .join(' ')

  return label || fallback
}

const CSV_LINE_BREAK = '\r\n'
const UTF8_BOM = '\uFEFF'

export const buildCsvRow = (values: Array<string | number | null | undefined | BulkField>) => {
  return values
    .map(value => {
      const cellValue = String(value ?? '').trim()
      const shouldQuote = /[",\n\r]/.test(cellValue)
      const escapedValue = cellValue.replace(/"/g, '""')

      return shouldQuote ? `"${escapedValue}"` : escapedValue
    })
    .join(',')
}

export const buildCsvHeaderRow = (headers: BulkField[]) => {
  return buildCsvRow(headers.map(field => field.name))
}

export const downloadCsvTemplate = (headers: BulkField[], filename = 'bulk-upload-sample.csv') => {
  const emptySampleRow = buildCsvRow(headers.map(() => ''))
  const csvContent = `${UTF8_BOM}${[buildCsvHeaderRow(headers), emptySampleRow].join(CSV_LINE_BREAK)}${CSV_LINE_BREAK}`
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = filename
  link.rel = 'noopener'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  // Revoking immediately can truncate the download on macOS (Numbers fails to open).
  window.setTimeout(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, 1500)
}

// type TableHeaderArrayOptions = {
//   excludeHeaders?: string[]
//   includeHeaders?: string[]
// }

// const DEFAULT_EXCLUDED_TABLE_HEADERS = new Set(['action', 'actions', ''])

// export const getTableHeaderArray = <TData, TValue = unknown>(
//   columns: ColumnDef<TData, TValue>[],
//   options?: TableHeaderArrayOptions
// ) => {
//   const excludedHeaders = new Set([
//     ...DEFAULT_EXCLUDED_TABLE_HEADERS,
//     ...(options?.excludeHeaders || []).map(header => header.trim().toLowerCase())
//   ])

//   const headers = columns.reduce<string[]>((acc, column) => {
//     if (typeof column.header !== 'string') return acc

//     const header = column.header.trim()
//     if (!header || excludedHeaders.has(header.toLowerCase())) return acc

//     acc.push(header)
//     return acc
//   }, [])

//   options?.includeHeaders?.forEach(header => {
//     const normalizedHeader = header.trim()
//     const alreadyIncluded = headers.some(item => item.toLowerCase() === normalizedHeader.toLowerCase())

//     if (normalizedHeader && !alreadyIncluded) {
//       headers.push(normalizedHeader)
//     }
//   })

//   return headers
// }

export const getKeys = (obj: Record<string, any>): string[] => {
  return Object.keys(obj)
}

export const getValues = (obj: Record<string, any>, key: string): string[] => {
  const values = obj[key]
  if (Array.isArray(values)) {
    return values.filter(item => typeof item === 'string')
  }
  return []
}

export const getEnumStrings = (enumObj: any): string[] => {
  return Object.keys(enumObj)?.map(key => enumObj[key])
}

export const GetProductName = (id: string) => {
  const { singleProduct } = useProducts({ id })
  return singleProduct ? `${singleProduct?.attributes?.generic_name} (${singleProduct?.attributes?.brand_name})` : null
}

export const GetFrequencyName = (id: string) => {
  const { patientId } = useParams<string>()
  const { medFrequencies } = useMedication({ patientId })

  return medFrequencies?.find(freq => freq?.id === id)?.attributes.code || null
}

export const GetPatientName = (id: string) => {
  const user = useAppSelector(state => state.user.user)

  const { patients } = usePatient({ id: user?.authable_id })

  const patient = patients?.find(patient => patient?.id === id)
  return patient ? `${patient.attributes.first_name} ${patient.attributes.last_name}` : '-'
}

export const getError = (error: any) => {
  const errors = error?.response?.data?.errors
  const message = error?.response?.data?.message || error?.message

  if (Array.isArray(errors) && errors.length > 0) {
    errors.forEach((item: any) => {
      if (typeof item === 'string') {
        showToast(item)
        return
      }
      showToast(item?.detail || item?.message || 'an error occurred')
    })
    return
  }

  showToast(message || 'an error occurred')
}

export const isAllergyError = (error: any) => {
  return error?.response?.data?.errors?.some((item: any) =>
    item.detail.toLowerCase().includes('nf drug name patient is allergic to this drug')
  )
}

// export const handleError = (error: any) => {
//   if (isAllergyError(error)) {

//   } else {
//     getError(error)
//   }
// }

export const getIncludedData = (data?: any[], type?: string, id?: string) => {
  return data?.find(item => item.type === type && item?.id === id)
}

export const getUnitName = (included?: AdmissionIncluded[], unitId?: string): string => {
  const unit = getIncludedData(included, 'unit', unitId)
  return unit?.attributes?.name || '-'
}

export function isArrayOfStrings(value: any) {
  return Array.isArray(value) && value.every(item => typeof item === 'string')
}

export const GetMedication = (id: string) => {
  const { patientId } = useParams<string>()
  const { medications } = useMedication({ patientId })
  return medications?.find(med => med?.id === id) || null
}

export function extractDynamicKeys(content: string): string[] {
  const regex = /{{\s*([\w.]+)\s*}}/g
  const keys = new Set<string>()
  let match
  while ((match = regex.exec(content)) !== null) {
    keys.add(match[1])
  }
  return Array.from(keys)
}

// Simulate LiquidJS functionality
export const engine = new Liquid({
  extname: '.liquid'
})

// Register custom medical filters
engine.registerFilter('highlightAbnormal', (value: any, normalRange: any) => {
  const [min, max] = normalRange.split('-')?.map(Number)
  if (value < min || value > max) {
    return `<span class="abnormal-value">${value} (Abnormal)</span>`
  }
  return value
})

engine.registerFilter('formatDate', (date: any) => {
  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
})

engine.registerFilter('formatTime', (date: any) => {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit'
  })
})

engine.registerFilter('capitalise', (value: string) => {
  return value.toUpperCase()
})

export const findStaffById = (includedDatas: NoteIncluded[] | undefined, staffId: string) => {
  return includedDatas?.find(({ type, id }) => type === 'staff' && id === staffId)?.attributes
}

export function removeLoinc(name: string) {
  return name.replace(/\s*\(LOINC:.*\)$/, '')
}

export function transformToLiquid(template: string, map: Record<string, any>) {
  return template.replace(/\[\[(\w+)\]\]/g, (match, key) => {
    return map[key] || match
  })
}

export function getVitalTemplate(key: string) {
  return {
    label: key.replace(/_/g, ' '),
    template: `[[${key}]]`
  }
}

export function flattenKeysAndValuesUnique(obj: Record<string, string[]>) {
  const keys = Object.keys(obj)
  const values = Object.values(obj).flat()
  const newValues = Array.from(new Set([...keys, ...values]))
  return newValues
}

type Country = {
  name: string
  code: string
  dial_code: string
}

export const sortCountriesByName = (countries: Country[]) => {
  return [...countries].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
}

export const parseCustomStrength = (inputString: string): Array<{ api_name: string; strength: string }> => {
  // Split the string by '+', trim whitespace from each part, and filter out empty strings
  const parts = inputString
    .split('+')
    ?.map(part => part.trim())
    .filter(Boolean)

  if (parts?.length === 0) {
    return []
  }

  // Map each part to the object structure
  return parts?.map(strength => ({
    api_name: 'custom', // Use a placeholder API name for custom entries
    strength: strength
    // earliest_expiry_date is omitted for custom entries as it's not applicable
  }))
}

export const concatenateStrengthsConcise = (apiDoses: ApiStrength[] | string | undefined) => {
  if (typeof apiDoses === 'string') return apiDoses || '-'

  return Array.isArray(apiDoses)
    ? apiDoses
        ?.map(d => d.strength)
        .filter(Boolean)
        .join('/')
    : '-'
}

export function addBatchCounts(inv: ProductInventoryResMock[] | undefined): ProductInventoryResMock[] {
  // Defensive copy to avoid mutating the original input
  const products = inv || []

  // --- PHASE 1: AGGREGATE ---
  const strengthBatchMap = new Map<string, Set<string>>()
  for (const product of products) {
    const batchNumber = product.attributes?.batch_number
    const strengths = product.attributes?.strength
      ? [{ api_name: product.attributes.drug_name, strength: product.attributes.strength }]
      : product.attributes?.api_strengths

    if (!batchNumber || !strengths) continue

    for (const strength of strengths) {
      const strengthKey = `${strength.strength} ${strength.api_name}`
      if (!strengthBatchMap.has(strengthKey)) {
        strengthBatchMap.set(strengthKey, new Set())
      }
      strengthBatchMap.get(strengthKey)!.add(batchNumber) // Use non-null assertion as we just set it
    }
  }
  const enrichedData = products.map(product => {
    // Use structuredClone for a modern, deep copy of the product object
    const newProduct = structuredClone(product)

    const strengths = newProduct.attributes?.strength
      ? [{ api_name: newProduct.attributes.drug_name, strength: newProduct.attributes.strength }]
      : newProduct.attributes?.api_strengths

    if (strengths) {
      // Find the maximum batch count for all strengths in this product
      const batchCounts = strengths.map(strength => {
        const strengthKey = `${strength.strength} ${strength.api_name}`
        return strengthBatchMap.get(strengthKey)?.size || 0
      })

      // If there are strengths, find the max count; otherwise, the count is 0.
      const maxBatchCount = batchCounts?.length > 0 ? Math.max(...batchCounts) : 0

      // CORRECTED: Add the batch_count to the attributes object, not the strengths.
      newProduct.attributes.batch_count = maxBatchCount
    }
    return newProduct
  })

  return enrichedData
}

export function deduplicateStrengthArrays(strengthArrays: string[][] | undefined) {
  if (!strengthArrays || !Array.isArray(strengthArrays)) {
    return []
  }

  const seenKeys = new Set()
  const uniqueStrengths = []

  for (const strengthArray of strengthArrays) {
    // Convert the array to a JSON string to create a unique, comparable key.
    const key = JSON.stringify(strengthArray)

    // If we haven't seen this key before, it's a unique array.
    if (!seenKeys.has(key)) {
      seenKeys.add(key)
      uniqueStrengths.push(strengthArray)
    }
  }

  return uniqueStrengths
}

export function formatNumber(n: number | null, fallback = '-') {
  if (n === null || isNaN(n)) return fallback
  return n.toLocaleString()
}

export function formatCurrency(n: number | null, currency = '₦', fallback = '-') {
  if (n === null || isNaN(n)) return fallback
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

export function formatDate(s: string | null | undefined, fallback = '-') {
  if (!s) return fallback
  const d = new Date(s)
  if (isNaN(d.getTime())) return fallback
  // Return date as yyyy-mm-dd
  return d.toISOString().slice(0, 10)
}

export const pluralize = (count: number, word: string, pluralForm?: string) => {
  if (count === 1) {
    return word
  }
  return pluralForm || `${word}s`
}

// truncate a string to a given length, appending ellipsis if trimmed
export function truncate(str: string, maxChars = 20): string {
  if (str.length <= maxChars) {
    return str
  }
  return `${str.slice(0, maxChars)}...`
}
